/**
 * Copyright 2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import {
  ExtendedTooltip,
  FlexContainer,
  MultipleSelect,
  SelectButton,
} from "@nordcloud/gnui";
import { getTitleText } from "~/tools";
import { ResourceDetailsActionDetailsSidebarFilterType } from "../../../types/ResourceDetailsActionDetailsSidebarState";
import { useResourceDetailsActionDetailsState } from "./ResourceDetailsActionDetailsSidebarProvider";
import { useGetFilteredActionDetails } from "./useGetFilteredActionDetails";

const filters = [
  {
    description: "",
    value: ResourceDetailsActionDetailsSidebarFilterType.ALL,
    label: "all",
    count: 0,
  },
  {
    description: "New package installed",
    value: ResourceDetailsActionDetailsSidebarFilterType.INSTALLED,
    label: "installed",
    count: 0,
  },
  {
    description: "Existing package has been updated with the new version",
    value: ResourceDetailsActionDetailsSidebarFilterType.UPDATED,
    label: "updated",
    count: 0,
  },
  {
    description:
      "Package is available for installation (currently not installed)",
    value: ResourceDetailsActionDetailsSidebarFilterType.AVAILABLE,
    label: "available",
    count: 0,
  },
  {
    description: "Package was uninstalled",
    value: ResourceDetailsActionDetailsSidebarFilterType.REMOVED,
    label: "removed",
    count: 0,
  },
  {
    description: "An error occurred while trying to install/update the package",
    value: ResourceDetailsActionDetailsSidebarFilterType.FAILED,
    label: "failed",
    count: 0,
  },
];

type Props = {
  eventId: string;
  eventActionId: string;
  resourceId: string;
};

export function ResourceDetailsActionDetailsSidebarHeaderFilters({
  eventId,
  eventActionId,
  resourceId,
}: Props) {
  const { actionDetailsState, updateActionDetailsState } =
    useResourceDetailsActionDetailsState();

  const { packagesCounts } = useGetFilteredActionDetails({
    eventId,
    eventActionId,
    resourceId,
  });

  const mergedFilters = filters.map((filter) => {
    return {
      ...filter,
      count: packagesCounts?.[filter.value] ?? 0,
    };
  });

  return (
    <FlexContainer grow={1}>
      <MultipleSelect size="small">
        {mergedFilters.map((filter) => (
          <ExtendedTooltip
            caption={filter?.description}
            position="start"
            key={filter.value}
          >
            <SelectButton
              name={filter.value}
              value={filter.value}
              labelText={`${getTitleText(filter.label)} (${filter.count})`}
              isActive={actionDetailsState.filterQuery === filter.value}
              onClick={() =>
                updateActionDetailsState({ filterQuery: filter.value })
              }
            />
          </ExtendedTooltip>
        ))}
      </MultipleSelect>
    </FlexContainer>
  );
}
